<p-treeTable
  (onFilter)="onFilter($event)"
  (onLazyLoad)="loadLazyData($event)"
  (onNodeCollapse)="expand(tt)"
  (onNodeExpand)="expand(tt)"
  (selectionChange)="selectionChange.emit($event)"
  [columns]="scrollableColumns"
  [dataKey]="dataKey"
  [filterMode]="strictFilterMode ? 'strict' : 'lenient'"
  [first]="first"
  [frozenColumns]="frozenColumns"
  [frozenWidth]="frozenWidth"
  [lazy]="lazy"
  [loading]="loading"
  [paginator]="pagination"
  [rows]="rowsPerPage"
  [rowTrackBy]="getTrackByFn(dataKey)"
  [scrollable]="true"
  [selection]="selection"
  [selectionMode]="selectable ? 'multiple' : 'none'"
  [showGridlines]="true"
  [sortField]="sortField"
  [sortOrder]="sortOrder"
  [totalRecords]="totalRecords"
  [value]="value"
  [virtualScroll]="!pagination"
  [virtualScrollItemSize]="50"
  #tt
  scrollHeight="flex"
  styleClass="flex-table p-treetable-sm p-treetable-striped">
  <!-- With virtual scrolling enabled the frozen column doesn't scroll at all
  Bug report: https://github.com/primefaces/primeng/issues/11722
  if/when issue is resolved, enable virtual scrolling again and compare performance -->
  <ng-template pTemplate="caption">
    <div [class.toolbar-single-row]="singleRowToolbar" class="toolbar">
      @if (hasFocusDate) {
        <p-datepicker
          (ngModelChange)="setFocusDate($event)"
          (onClearClick)="setFocusDate()"
          (onSelect)="setFocusDate($event)"
          [ngModel]="focusDate()"
          [ngModelOptions]="{ updateOn: 'blur' }"
          [readonlyInput]="!focusDateClearable"
          [showButtonBar]="focusDateClearable"
          [showIcon]="true"
          [style]="{ width: '140px' }"
          appendTo="body"
          dateFormat="dd.mm.yy"
          yearRange="2000:2030"></p-datepicker>
      }

      @if (showFrozenColumnSelection) {
        <p-multiSelect
          [(ngModel)]="frozenColumns"
          (onChange)="frozenColumnsChange()"
          [options]="columns"
          [style]="{ minWidth: '300px', maxWidth: '300px' }"
          optionLabel="header"></p-multiSelect>
      }

      @if (hasFocusYear) {
        <p-select
          (ngModelChange)="focusYear.set($event)"
          [ngModel]="focusYear()"
          [options]="focusYearRange"
          [style]="{ width: '140px' }"
          appendTo="body"
          appScrollIntoView></p-select>
      }

      @if (editable) {
        <p-button
          (onClick)="save()"
          [disabled]="isSaveDisabled"
          [label]="'ACTIONS.SAVE' | translate"
          icon="fal fa-save"
          type="button"></p-button>
        <p-button
          (onClick)="cancel()"
          [disabled]="isCancelDisabled"
          [label]="'ACTIONS.CANCEL' | translate"
          icon="fal fa-times"
          type="button"></p-button>
        @if (isCreateAllowed) {
          <p-button
            (onClick)="create()"
            [disabled]="isCreateDisabled"
            [label]="createLabels[0] | translate"
            icon="fal fa-plus"
            type="button"></p-button>
        }
      }

      @if (focusLabelKey && hierarchyLevels) {
        <p-button
          (onClick)="showHierarchyMenu($event)"
          [label]="focusLabelKey | translate"
          icon="fal fa-folder-tree"
          type="button"></p-button>
      }

      @for (button of toolbarButtons; track button.labelKey) {
        <p-button
          (onClick)="button.onClick.emit($event)"
          [disabled]="button.disabled"
          [icon]="button.icon"
          [label]="button.labelKey | translate"
          type="button"></p-button>
      }

      @if (selectable) {
        <p-button
          (onClick)="clearSelection()"
          [disabled]="selection?.length === 0"
          [label]="'ACTIONS.CLEAR_SELECTION' | translate: { count: selection.length }"
          icon="fal fa-square"
          type="button"></p-button>
      }

      <div class="spacer"></div>

      <p-button
        (onClick)="resetFilter()"
        [label]="'GLOBAL.RESET_FILTER' | translate"
        icon="fal fa-undo"
        iconPos="left"
        styleClass="p-button-outlined"
        type="button"></p-button>

      @if (rodixAdmin) {
        <p-button
          (onClick)="exportCSV(getFlatTreeData(tt.filteredNodes || (tt.value ?? [])))"
          icon="fal fa-file-alt"
          iconPos="left"
          label="CSV"
          styleClass="p-button-outlined"
          type="button"></p-button>

        <p-button
          (onClick)="exportExcel(getFlatTreeData(tt.filteredNodes || (tt.value ?? [])))"
          icon="fal fa-file-excel"
          iconPos="left"
          label="Excel"
          styleClass="p-button-outlined"
          type="button"></p-button>
      }
    </div>
  </ng-template>

  <ng-template let-columns pTemplate="header">
    @if (!newHeaderGroupMode) {
      @if (headerGroups.length > 0 && columns === scrollableColumns) {
        <tr style="height: 33px">
          @for (header of getHeaders(); track $index) {
            <th [colSpan]="header.colSpan" style="position: relative">
              <div appEllipsisTooltip class="header-ellipsis">{{ header.title }}</div>
            </th>
          }
          @if (actionColumnWidthInPx) {
            <th></th>
          }
        </tr>
      }
      @if (headerGroups.length > 0 && columns === frozenColumns) {
        <tr style="height: 33px">
          <th [colSpan]="frozenColumns.length"></th>
        </tr>
      }
    }

    @if (newHeaderGroupMode) {
      @if (headerGroups.length > 0 && columns === scrollableColumns) {
        @for (groups of calculatedHeaderGroups; track $index) {
          <tr style="height: 33px">
            @for (group of groups; track $index) {
              <th [colSpan]="group.colSpan" [rowSpan]="group.rowSpan || 1" style="position: relative">
                <div appEllipsisTooltip class="header-ellipsis">{{ group.title }}</div>
              </th>
            }
          </tr>
        }
      }
      @if (headerGroups.length > 0 && columns === frozenColumns) {
        @for (groups of headerGroups; track $index) {
          <tr style="height: 33px">
            <th [colSpan]="frozenColumns.length"></th>
          </tr>
        }
      }
    }

    <tr style="height: 33px">
      @for (col of columns; track col.field) {
        <th [style.min-width]="col.minWidth || col.width" [style.width]="col.width" style="position: relative">
          <div appEllipsisTooltip class="header-ellipsis">{{ col.header }}</div>
          @if (!col.disableFilterAndSort) {
            <p-treeTableSortIcon
              (click)="tt.sort({ field: col.field })"
              [field]="col.field"
              [style]="{ position: 'absolute', right: '15px', top: '7px' }"></p-treeTableSortIcon>
          }
        </th>
      }
      @if (actionColumnWidthInPx && columns === scrollableColumns) {
        <th></th>
      }
    </tr>
    <tr style="height: 53px">
      @for (col of columns; track col.field) {
        <th>
          @if (!col.disableFilterAndSort) {
            @switch (col.filterType) {
              @case ('date') {
                <p-datepicker
                  (ngModelChange)="tt.filter($event, col.field, 'date')"
                  [ngModel]="getDateFilter(col.field)"
                  [showButtonBar]="true"
                  appendTo="body"
                  dateFormat="dd.mm.yy"></p-datepicker>
              }
              @case ('boolean') {
                <div
                  (click)="cycleTriStateFilter($event, tt, col.field)"
                  style="width: 100%; text-align: center; cursor: pointer">
                  <p-checkbox
                    [binary]="true"
                    [indeterminate]="filters[col.field]?.value === undefined || filters[col.field]?.value === null"
                    [ngModel]="filters[col.field]?.value === true"
                    [readonly]="true"
                    [style]="{ 'pointer-events': 'none' }"></p-checkbox>
                </div>
              }
              @case ('percent') {
                <p-inputNumber
                  (onInput)="tt.filter(toNumber($event) / 100 + '', col.field, 'equals')"
                  [max]="100"
                  [maxFractionDigits]="2"
                  [min]="0"
                  [minFractionDigits]="2"
                  [ngModel]="filters[col.field]?.value"
                  [showButtons]="false"
                  [suffix]="'%'"></p-inputNumber>
              }
              @case ('time') {
                <p-datepicker
                  (ngModelChange)="tt.filter($event, col.field, 'equals')"
                  [ngModel]="getDateFilter(col.field)"
                  [timeOnly]="true"
                  appendTo="body"></p-datepicker>
              }
              @case ('dateRangeAssignment') {
                <div
                  (click)="cycleTriStateFilter($event, tt, col.field)"
                  style="width: 100%; text-align: center; cursor: pointer">
                  <p-checkbox
                    [binary]="true"
                    [indeterminate]="filters[col.field]?.value === undefined || filters[col.field]?.value === null"
                    [ngModel]="filters[col.field]?.value === true"
                    [readonly]="true"
                    [style]="{ 'pointer-events': 'none' }"></p-checkbox>
                </div>
              }
              @default {
                @if (col.options) {
                  <p-select
                    (onChange)="tt.filter($event.value, col.field, 'equals')"
                    [filter]="true"
                    [filterBy]="'label'"
                    [ngModel]="filters[col.field]?.value"
                    [options]="col.options"
                    [placeholder]="'-'"
                    [showClear]="true"
                    appendTo="body"
                    appScrollIntoView></p-select>
                } @else {
                  <input
                    (input)="tt.filter($any($event.target).value, col.field, 'contains')"
                    [ngModel]="filters[col.field]?.value"
                    pInputText
                    type="text" />
                }
                <ng-template #text>
                  <input
                    (input)="tt.filter($any($event.target).value, col.field, 'contains')"
                    [ngModel]="filters[col.field]?.value"
                    pInputText
                    type="text" />
                </ng-template>
              }
            }
          }
        </th>
      }
      @if (actionColumnWidthInPx && columns === scrollableColumns) {
        <th
          [style.max-width]="actionColumnWidthInPx"
          [style.min-width]="actionColumnWidthInPx"
          [style.width]="actionColumnWidthInPx"></th>
      }
    </tr>
  </ng-template>
  <ng-template let-columns="columns" let-node="node" let-rowData="rowData" let-rowNode pTemplate="body">
    <tr
      [attr.aria-busy]="rowData?.deleting || null"
      [class.row-deleting]="rowData?.deleting"
      [class.row-inactive]="rowData?.inactive"
      [style.pointer-events]="node.selectable === false ? 'none' : 'all'"
      [ttSelectableRow]="rowNode">
      @for (column of columns; track column.field; let columnIndex = $index; let last = $last) {
        <td
          [class.invisible-border-right]="
            shouldHaveInvisibleBorderRight(last, column, rowNode, columnIndex + frozenColumns.length) &&
            headerGroups.length > 0 &&
            !newHeaderGroupMode
          "
          [class.not-matching-level]="
            !hasMatchingLevel(column, rowNode) && headerGroups.length > 0 && !newHeaderGroupMode
          "
          [style.max-width]="column.width"
          [style.min-width]="column.minWidth || column.width"
          [style.width]="column.width"
          [style]="rowData['_style_' + column.field]"
          style="height: 50px">
          @if (hasMatchingLevel(column, rowNode) || headerGroups.length === 0 || newHeaderGroupMode) {
            <div class="table-cell">
              @if (columnIndex === 0 && frozenColumns.length === 0) {
                <p-treeTableToggler [rowNode]="rowNode"></p-treeTableToggler>
              }
              <app-table-cell
                (valueChange)="update($event, node, column.field)"
                [changed]="rowData._changes && rowData._changes[column.field] !== undefined"
                [column]="column"
                [editable]="
                  column.editable &&
                  ((column.onlyInitialModificationAllowed && rowData._new) || !column.onlyInitialModificationAllowed) &&
                  isEditable(node, column)
                "
                [error]="rowData._errors && rowData._errors[column.field]"
                [options]="column.options"
                [required]="column.required"
                [route]="getRouterLink(column.route, rowData)"
                [row]="node"
                [type]="column.filterType"
                [url]="getUrl(rowData, column.url)"
                [value]="getData(rowData, column.field)"></app-table-cell>
            </div>
          }
        </td>
      }
      @if (actionColumnWidthInPx) {
        <td
          [style.max-width]="actionColumnWidthInPx"
          [style.min-width]="actionColumnWidthInPx"
          [style.width]="actionColumnWidthInPx"
          style="white-space: nowrap">
          @if (editable) {
            @if (rowData.deletable === null || rowData.deletable === undefined || rowData.deletable === true) {
              <p-button
                (onClick)="delete(rowNode.node, rowNode.level)"
                [label]="'ACTIONS.DELETE' | translate"
                [style]="{ 'margin-right': '0.5rem' }"
                icon="fal fa-trash"
                size="small"
                styleClass="p-button-danger"
                type="button"></p-button>
            }
            @if (isCreateAllowed && (rowNode.level + 1 < createLabels.length || deepNestingAllowed())) {
              <p-button
                (onClick)="create(rowNode.node)"
                [label]="getNestedNodeLabel(rowNode.level) | translate"
                [style]="{ 'margin-right': '0.5rem' }"
                icon="fal fa-plus"
                size="small"
                styleClass="p-button-primary p-button-outlined"
                type="button"></p-button>
            }
          }
          @for (button of actionButtons; track button.labelKey) {
            @if (
              (!button.displayIf || getData(rowNode, button.displayIf)) &&
              (!button.displayIfNot || !getData(rowNode, button.displayIfNot))
            ) {
              <p-button
                (onClick)="button.onClick.emit({ event: $event, row: rowNode.node })"
                [disabled]="button.disabled"
                [icon]="button.icon"
                [label]="button.labelKey | translate"
                [style]="{ 'margin-right': '0.5rem' }"
                [styleClass]="'p-button-' + button.type + (button.type === 'primary' ? ' p-button-outlined' : '')"
                size="small"
                type="button"></p-button>
            }
          }
        </td>
      }
    </tr>
  </ng-template>
  <ng-template let-columns="columns" let-node="node" let-rowData="rowData" let-rowNode pTemplate="frozenbody">
    <tr
      [attr.aria-busy]="rowData?.deleting || null"
      [class.row-deleting]="rowData?.deleting"
      [class.row-inactive]="rowData?.inactive">
      @for (column of columns; track column.field; let columnIndex = $index; let last = $last) {
        <td
          [class.not-matching-level]="
            !hasMatchingLevel(column, rowNode) && headerGroups.length > 0 && !newHeaderGroupMode
          "
          [style.min-width]="column.minWidth || column.width"
          [style.width]="column.width"
          [style]="rowData['_style_' + column.field]"
          style="height: 50px">
          <div class="table-cell">
            @if (columnIndex === 0 && frozenColumns.length > 0) {
              <p-treeTableToggler [rowNode]="rowNode"></p-treeTableToggler>
            }
            @if (hasMatchingLevel(column, rowNode) || headerGroups.length === 0 || newHeaderGroupMode) {
              <app-table-cell
                (valueChange)="update($event, node, column.field)"
                [changed]="rowData._changes && rowData._changes[column.field] !== undefined"
                [column]="column"
                [editable]="
                  column.editable &&
                  ((column.onlyInitialModificationAllowed && rowData._new) || !column.onlyInitialModificationAllowed) &&
                  isEditable(node, column)
                "
                [error]="rowData._errors && rowData._errors[column.field]"
                [options]="column.options"
                [required]="column.required"
                [type]="column.filterType"
                [value]="getData(rowData, column.field)"></app-table-cell>
            }
          </div>
        </td>
      }
    </tr>
  </ng-template>

  <ng-template pTemplate="emptymessage">
    @if (!loading) {
      <tr>
        <td [attr.colspan]="columns.length + 2" class="table-empty-message">{{ 'GLOBAL.NO_ENTRIES' | translate }}</td>
      </tr>
    }
  </ng-template>

  @if (footer; as columns) {
    <ng-template let-columns pTemplate="footer">
      <tr>
        @for (col of columns; track col.field) {
          <td
            [style.color]="col.footerColor"
            [style.max-width]="col.width"
            [style.min-width]="col.minWidth || col.width"
            [style.width]="col.width">
            @if (col.footerKey) {
              {{ col.footerKey | translate }}
            }
            @if (col.footer) {
              {{ col.footer() }}
            }
          </td>
        }
      </tr>
    </ng-template>
  }
</p-treeTable>

@if (focusLabelKey && hierarchyLevels) {
  <p-tieredMenu
    (click)="hierarchyMenu.hide()"
    [model]="hierarchyMenuModel"
    [popup]="true"
    [style]="{ width: '300px' }"
    #hierarchyMenu></p-tieredMenu>
}
